import { put, call, takeLatest, takeEvery } from 'redux-saga/effects'
import { AsyncAction } from 'src/utils/reduxUtils'
import { Endpoints } from 'src/constants/endpoints'
import { TeamMemberInvite } from 'src/interfaces/team'
import {
  FetchTeamMemberInvite,
  FetchTeamMemberInvites,
  FetchTeamMemberInvitesCount,
  ResendTeamMemberInvite,
  SendInviteToTeamMember,
  UpdateTeamMemberInviteAccess,
} from 'src/constants/actionTypes'
import { callSecureApi, ApiService } from 'src/sagas/api/callApi'

export type FetchTeamMemberInvitesPayload = {
  search?: string
  sort?: string
}

function* onFetchTeamMemberInvites(
  action: AsyncAction<FetchTeamMemberInvitesPayload, { data: Array<TeamMemberInvite> }>,
) {
  const { search, sort } = action.payload

  try {
    const { payload }: { payload: { data: Array<TeamMemberInvite> } } = yield call(
      callSecureApi,
      Endpoints.FetchTeamMemberInvites,
      {
        apiService: ApiService.Paraworks,
        query: { search, sort, limit: 10000 },
      },
    )

    yield put(FetchTeamMemberInvites.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to fetch team member invites'

    yield put(FetchTeamMemberInvites.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

function* onFetchTeamMemberInvitesCount(action: AsyncAction<null, number>) {
  try {
    const { payload }: { payload: number } = yield call(
      callSecureApi,
      Endpoints.FetchTeamMemberInvitesCount,
      {
        apiService: ApiService.Paraworks,
      },
    )

    yield put(FetchTeamMemberInvitesCount.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to fetch team member invites count'

    yield put(FetchTeamMemberInvitesCount.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface SendInviteToTeamMemberRequest {
  email: string
  firstName: string
  lastName: string
  roleId: string
  allMarkets: boolean
  marketIds: string[]
}

function* onSendInviteToTeamMember(action: AsyncAction<SendInviteToTeamMemberRequest>) {
  const { email, firstName, lastName, roleId, allMarkets, marketIds } = action.payload

  try {
    const { payload }: { payload: { id: string } } = yield call(
      callSecureApi,
      Endpoints.SendInviteToTeamMember,
      {
        method: 'POST',
        apiService: ApiService.Paraworks,
        body: { email, firstName, lastName, roleId, allMarkets, marketIds },
      },
    )

    yield put(SendInviteToTeamMember.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to invite team member'

    yield put(SendInviteToTeamMember.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface ResendTeamMemberInviteRequest {
  teamMemberInviteId: string
}

function* onResendTeamMemberInvite(action: AsyncAction<ResendTeamMemberInviteRequest>) {
  const { teamMemberInviteId } = action.payload

  try {
    const { payload }: { payload: { id: string } } = yield call(
      callSecureApi,
      Endpoints.ResendTeamMemberInvite,
      {
        method: 'POST',
        apiService: ApiService.Paraworks,
        params: [{ field: ':teamMemberInviteId', value: teamMemberInviteId }],
      },
    )

    yield put(ResendTeamMemberInvite.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to resend team member invite'

    yield put(ResendTeamMemberInvite.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface FetchTeamMemberInviteRequest {
  teamMemberInviteId: string
}

function* onFetchTeamMemberInvite(action: AsyncAction<FetchTeamMemberInviteRequest>) {
  const { teamMemberInviteId } = action.payload

  try {
    const { payload }: { payload: { id: string } } = yield call(
      callSecureApi,
      Endpoints.FetchTeamMemberInvite,
      {
        method: 'GET',
        apiService: ApiService.Paraworks,
        params: [{ field: ':teamMemberInviteId', value: teamMemberInviteId }],
      },
    )

    yield put(FetchTeamMemberInvite.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to fetch team member invite'

    yield put(FetchTeamMemberInvite.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface UpdateTeamMemberInviteAccessRequest {
  teamMemberInviteId: string
  teamRoleId?: string
  allMarkets?: boolean
  marketIds?: string[]
}
function* onUpdateTeamMemberInviteAccess(action: AsyncAction<UpdateTeamMemberInviteAccessRequest>) {
  const { teamRoleId, allMarkets, marketIds, teamMemberInviteId } = action.payload

  try {
    const { payload }: { payload: null } = yield call(
      callSecureApi,
      Endpoints.UpdateTeamMemberInviteAccess,
      {
        method: 'PATCH',
        apiService: ApiService.Paraworks,
        body: { roleId: teamRoleId, allMarkets, marketIds },
        params: [{ field: ':teamMemberInviteId', value: teamMemberInviteId }],
      },
    )

    yield put(UpdateTeamMemberInviteAccess.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to update team member invite access'

    yield put(UpdateTeamMemberInviteAccess.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

export function* teamMemberInvitesSaga() {
  yield takeLatest(FetchTeamMemberInvites.type.REQUEST, onFetchTeamMemberInvites)
  yield takeEvery(FetchTeamMemberInvitesCount.type.REQUEST, onFetchTeamMemberInvitesCount)
  yield takeEvery(SendInviteToTeamMember.type.REQUEST, onSendInviteToTeamMember)
  yield takeEvery(ResendTeamMemberInvite.type.REQUEST, onResendTeamMemberInvite)
  yield takeEvery(FetchTeamMemberInvite.type.REQUEST, onFetchTeamMemberInvite)
  yield takeEvery(UpdateTeamMemberInviteAccess.type.REQUEST, onUpdateTeamMemberInviteAccess)
}
